import React, { useReducer, useEffect } from 'react';
import classNames from 'classnames';
import { Button, ButtonGroup, Intent, HTMLSelect } from '@blueprintjs/core';
import { FormattedMessage as T } from '@/components/FormattedMessage';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import { range } from 'lodash';
import { Icon } from '@/components/Icon';
import { formattedNumber } from '@/utils';

import '@/style/components/DataTable/Pagination.scss';

const TYPE = {
  PAGE_CHANGE: 'PAGE_CHANGE',
  PAGE_SIZE_CHANGE: 'PAGE_SIZE_CHANGE',
  INITIALIZE: 'INITIALIZE',
};

interface PaginationState {
  currentPage: number;
  size: number;
  total: number;
  pages: number[];
  totalPages: number;
}

interface PaginationAction {
  type: string;
  page?: number;
  size?: number;
  total?: number;
}

const getState = ({
  currentPage,
  size,
  total,
}: {
  currentPage: number;
  size: number;
  total: number;
}): PaginationState => {
  const totalPages = Math.ceil(total / size);
  const visibleItems = 5;
  const halfVisibleItems = Math.ceil(visibleItems / 2);

  // create an array of pages to ng-repeat in the pager control
  let startPage: number, endPage: number;
  if (totalPages <= visibleItems) {
    // less than {visibleItems} total pages so show
    startPage = 1;
    endPage = totalPages;
  } else {
    // more than {visibleItems} total pages so calculate start and end pages
    if (currentPage <= halfVisibleItems) {
      startPage = 1;
      endPage = visibleItems;
    } else if (currentPage + (halfVisibleItems - 1) >= totalPages) {
      startPage = totalPages - (visibleItems - 1);
      endPage = totalPages;
    } else {
      startPage = currentPage - halfVisibleItems;
      endPage = currentPage + halfVisibleItems - 1;
    }
  }
  const pages = Array.from(
    { length: endPage + 1 - startPage },
    (_, i) => startPage + i,
  );

  // Too large or small currentPage
  let correctCurrentpage = currentPage;
  if (currentPage > totalPages) correctCurrentpage = totalPages;
  if (currentPage <= 0) correctCurrentpage = 1;

  return {
    currentPage: correctCurrentpage,
    size,
    total,
    pages,
    totalPages,
  };
};

/**
 * Plage de lignes REELLEMENT affichee par la page courante (F45).
 *
 * Le pied de tableau annoncait « Affichage de 1 a 3 sur 55 entrees » : il
 * injectait `currentPage` et `totalPages` (des numeros de PAGE) dans un
 * message qui decrit une plage de LIGNES. Sur un tableau de 3 pages, « 3 »
 * etait donc lu comme « ligne 3 ». La plage se derive maintenant de la meme
 * source que le total (`total` et `size` de l'etat de pagination), donc les
 * quatre chiffres du pied ne peuvent plus se contredire.
 */
export const getPaginationRange = (
  currentPage: number,
  size: number,
  total: number,
): { from: number; to: number } => {
  const safeTotal = Math.max(0, Number(total) || 0);
  const safeSize = Math.max(1, Number(size) || 1);
  const safePage = Math.max(1, Number(currentPage) || 1);

  if (safeTotal === 0) return { from: 0, to: 0 };

  const from = Math.min((safePage - 1) * safeSize + 1, safeTotal);
  const to = Math.min(safePage * safeSize, safeTotal);

  return { from, to };
};

/**
 * Total d'enregistrements a afficher dans le pied de tableau (F45).
 *
 * Trois sources possibles, par ordre de confiance :
 *   1. `total` fourni par la page (le vrai total renvoye par l'API) ;
 *   2. le nombre de lignes reellement chargees, en pagination client ;
 *   3. `pageSize x pageCount`, une ESTIMATION arrondie a la page — 20 x 3 = 60
 *      pour 55 enregistrements. C'est ce dernier calcul qui faisait mentir le
 *      pied ; il ne sert plus que de dernier recours, quand la page ne passe
 *      que `pagesCount` au `DataTable`.
 *
 * `null`, `undefined`, `NaN` et les negatifs ne sont PAS des totaux : ils
 * doivent retomber sur l'estimation, sans quoi une liste dont le total n'est
 * pas encore charge annoncerait « 0 entree ».
 */
export const resolvePaginationTotal = ({
  controlledTotal,
  manualPagination,
  pageSize,
  pageCount,
  rowsLength,
}: {
  controlledTotal?: unknown;
  manualPagination: boolean;
  pageSize: number;
  pageCount: number;
  rowsLength: number;
}): number => {
  const asNumber =
    typeof controlledTotal === 'number'
      ? controlledTotal
      : typeof controlledTotal === 'string' && controlledTotal.trim() !== ''
        ? Number(controlledTotal)
        : NaN;

  if (Number.isFinite(asNumber) && asNumber >= 0) return Math.floor(asNumber);

  // Pagination client : toutes les lignes sont deja la, on les compte.
  if (!manualPagination) return Math.max(0, rowsLength || 0);

  // Dernier recours : estimation par le nombre de pages. `pageCount` vaut -1
  // quand react-table ne le connait pas encore — mieux vaut 0 qu'un negatif.
  const estimated = (pageSize || 0) * (pageCount || 0);
  return estimated > 0 ? estimated : 0;
};

const reducer = (
  state: PaginationState,
  action: PaginationAction,
): PaginationState => {
  switch (action.type) {
    case TYPE.PAGE_CHANGE:
      return getState({
        currentPage: action.page as number,
        size: state.size,
        total: state.total,
      });
    case TYPE.PAGE_SIZE_CHANGE:
      return getState({
        currentPage: state.currentPage,
        size: action.size as number,
        total: state.total,
      });
    case TYPE.INITIALIZE:
      return getState({
        currentPage: action.page as number,
        size: action.size as number,
        total: action.total as number,
      });
    default:
      throw new Error();
  }
};

export function Pagination({
  currentPage,
  total,
  size,
  pageSizesOptions = [20, 30, 50, 75, 100, 150],
  onPageChange,
  onPageSizeChange,
}: {
  currentPage: number;
  total: number;
  size: number;
  pageSizesOptions?: number[];
  onPageChange: (payload: { page: number; pageSize: number }) => void;
  onPageSizeChange: (payload: { pageSize: number; page: number }) => void;
}) {
  const [state, dispatch] = useReducer(
    reducer,
    { currentPage, total, size },
    getState,
  );

  useEffect(() => {
    dispatch({
      type: TYPE.INITIALIZE,
      total,
      size,
      page: currentPage,
    });
  }, [total, size, currentPage]);

  // Le pied derive de l'ETAT de pagination — la meme source que la liste des
  // pages et que le total — et non plus d'un melange de props et d'index.
  const paginationRange = getPaginationRange(
    state.currentPage,
    state.size,
    state.total,
  );

  return (
    <div className="pagination">
      <div className="pagination__buttons-group">
        <ButtonGroup>
          <Button
            disabled={state.currentPage <= 1}
            onClick={() => {
              dispatch({ type: 'PAGE_CHANGE', page: state.currentPage - 1 });

              const page = state.currentPage - 1;
              const { size: pageSize } = state;

              onPageChange({ page, pageSize });
            }}
            minimal={true}
            className={'pagination__item pagination__item--previous'}
            icon={<Icon icon={'arrow-back-24'} iconSize={12} />}
          >
            <T id="previous" />
          </Button>

          {state.pages.map((page) => (
            <Button
              key={page}
              intent={state.currentPage === page ? Intent.PRIMARY : Intent.NONE}
              disabled={state.currentPage === page}
              onClick={() => {
                dispatch({ type: 'PAGE_CHANGE', page });
                const { size: pageSize } = state;

                onPageChange({ page, pageSize });
              }}
              minimal={true}
              className={classNames(
                'pagination__item',
                'pagination__item--page',
                {
                  'is-active': state.currentPage === page,
                },
              )}
            >
              {page}
            </Button>
          ))}
          <Button
            disabled={state.currentPage === state.totalPages}
            onClick={() => {
              dispatch({
                type: 'PAGE_CHANGE',
                page: state.currentPage + 1,
              });
              const page = state.currentPage + 1;
              const { size: pageSize } = state;

              onPageChange({ page, pageSize });
            }}
            minimal={true}
            className={'pagination__item pagination__item--next'}
            icon={<Icon icon={'arrow-forward-24'} iconSize={12} />}
          >
            <T id="next" />
          </Button>
        </ButtonGroup>
      </div>

      <div className="pagination__controls">
        <div className="pagination__goto-control">
          Go to
          <HTMLSelect
            minimal={true}
            options={range(1, state.totalPages + 1)}
            value={state.currentPage}
            onChange={(event) => {
              const page = parseInt(event.currentTarget.value, 10);
              const { size: pageSize } = state;

              dispatch({ type: 'PAGE_CHANGE', page });
              onPageChange({ page, pageSize });
            }}
          />
        </div>

        <div className="pagination__pagesize-control">
          <T id={'page_size'} />
          <HTMLSelect
            minimal={true}
            options={pageSizesOptions}
            value={size}
            onChange={(event) => {
              const pageSize = parseInt(event.currentTarget.value, 10);
              dispatch({ type: 'PAGE_SIZE_CHANGE', size: pageSize });
              dispatch({ type: 'PAGE_CHANGE', page: 1 });

              onPageSizeChange({ pageSize, page: 1 });
            }}
          />
        </div>
      </div>

      <div className="pagination__info">
        {intl.get('showing_range_to_total', {
          from: formattedNumber(paginationRange.from),
          to: formattedNumber(paginationRange.to),
          total: formattedNumber(state.total),
        })}
      </div>
    </div>
  );
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  size: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
};

Pagination.defaultProps = {
  currentPage: 1,
  size: 25,
};
